import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import React from "react";
import { TNextOfKinFormValues } from "../../../../models/Onboarding/onboarding";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import { saveEmergencyInfoThunk } from "../../../../store/onboarding/thunk";
import { createValidationSchema } from "../../../../utils/createValidationSchema";
import CustomButton from "../../../UI/Buttons/CustomButton";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import TextInput from "../../../UI/Inputs/TextInput";
import { showNotificationAction } from "../../../../store/application/slice";

interface EditNextOfKinProps {
  handleClose: (value: boolean) => void;
}

const EditNextOfKin: React.FC<EditNextOfKinProps> = ({ handleClose }) => {
  const dispatch = useDispatch();
  const inProgress = useSelector((state) => state.onboarding.inProgress);
  const emergencyInfo = useSelector(
    (state) => state.onboarding.emergencyinfodetails
  );

  /** Submit the form for personal-details */
  const { handleSubmit, values, errors, touched, handleChange } = useFormik({
    validationSchema: createValidationSchema("nextOfKin"),
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      first_name: emergencyInfo !== null ? emergencyInfo.first_name : "",
      last_name: emergencyInfo !== null ? emergencyInfo.last_name : "",
      phone_number: emergencyInfo !== null ? emergencyInfo.phone_number : "",
      relationship: emergencyInfo !== null ? emergencyInfo.relationship : "",
    },
    onSubmit: async (values: TNextOfKinFormValues) => {
      const payload = { ...values };
      try {
        const result = await dispatch(saveEmergencyInfoThunk(payload));
        if (isFulfilled(result)) {
          dispatch(showNotificationAction("Saved"))
          handleClose(false);
        }
      } catch (error) {
        throw new Error(
          "Could not connect to services at the moment. Please try again. Thanks."
        );
      }
    },
  });

  return (
    <div className="d-flex flex-column gap-15 fw-400 text-black">
      <div className="fs-22">Edit Next of Kin</div>
      <div className="w-100 h-1px"></div>
      <div className="fs-16 fw-400 text-black">
        Please provide details for your next of kin who should be contacted in
        the event of an emergency.
      </div>
      <div className="d-flex flex-column flex-lg-row gap-10">
        <div className="flex-fill">
          <TextInput
            value={values.first_name}
            field={"first_name"}
            label={"First Name"}
            onChange={handleChange}
            touched={touched.first_name! as boolean}
            error={errors.first_name as string}
            placeholder={" " + ""}
          />
        </div>
        <div className="flex-fill">
          <TextInput
            value={values.last_name}
            field={"last_name"}
            label={"Last Name"}
            onChange={handleChange}
            touched={touched.last_name! as boolean}
            error={errors.last_name as string}
            placeholder={" " + ""}
          />
        </div>
      </div>
      <div className="d-flex flex-column flex-lg-row gap-10">
        <div className="w-180px mobile-w-100">
          <TextInput
            value={values.phone_number}
            field={"phone_number"}
            label={"Phone Number"}
            onChange={handleChange}
            touched={touched.phone_number! as boolean}
            error={errors.phone_number as string}
            placeholder={" " + ""}
          />
        </div>
        <div className="flex-fill">
          <TextInput
            value={values.relationship}
            field={"relationship"}
            label={"Relationship"}
            onChange={handleChange}
            touched={touched.relationship! as boolean}
            error={errors.relationship as string}
            placeholder={" " + ""}
          />
        </div>
      </div>
      <div className="d-flex justify-content-end">
        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-teal text-black"
          onClick={handleSubmit}
          isLoading={inProgress}
          contentClass="d-flex"
        >
          <RoundedTick color={inProgress ? "#aaaaaa" : "black"} />
          Save
        </CustomButton>
      </div>
    </div>
  );
};

export default EditNextOfKin;
